import * as React from "react";
import * as moment from "moment";
import { useAuth } from "react-oidc-context";
import { DlcoAnalysisSet, FvcAnalysisSet, FvcSession, MepAnalysisSet, MipAnalysisSet, PefAnalysisSet, PefSession, SnipAnalysisSet, StudyScheduleVisit, WebPortalClient } from 'webportalclient';
import { ProcessCodedExceptionResponse, ProcessFetchResponse } from "./ProcessHttpResponse";
import { ParameterEntity } from "./SerenityReferenceData";
import { Gender, SmokingStatus, User, Language, TableParameters, StudyCoughSetting, Permission, Query, StudyFvcSetting, StudySvcSetting, StudyPulseOximetrySetting, StudyPcfSetting, StudyEcgSetting, StudyDlcoSetting, StudyDiarySetting, StudyPressureSetting, StudySubjectSetting, StudyLungVolumeSetting, ReportWidget, EDiaryConfiguration, AlertNotification, AnalysisBatch, AuditTrailEntity, AuditTrailAction, AuditTrailEvent, QueryFilter, BloodGlucoseSession, CoughReOpenComments, CoughAnalysisSetReOpenComment, ModelWithError, CoughAnalysisSetType, CoughAnalysisSetFixedComments, CoughAnalysisSet, CoughAnalysisSetEvent, CoughSession, VitaloJakCardAvailablity, SessionNote, DcfRequest, DcfRequestStatus, DeviceAllocation, Device, DeviceTypeInteraction, SessionMetadata, DlcoSessionMetadata, OverreadStatusEnum, TestAcceptabilityMutation, FixedCommentMutation, OverreadStatus, AnalysisSetType, Session, SubjectNote, OscillometryAnalysisSet, OscillometryAnalysisSetTypeModel, OverreadFixedComment, OverreadFixedCommentSet, Report, ReportingServicesCatalogItem, ImportSummaryResult, Setting, Site, SpirometryAnalysisSet, SpirometrySessionMetadata, OverreadChallengeStatus, Subject, StudyCoughEventType, DeviceType, StudyOverreadFixedCommentSet, StudyPopulationGroup, SessionType, SubjectType, SvcAnalysisSet, Visit, VisitScheduleItem, SessionScheduleItem, EDiarySessionScheduleItem, Phase, EDiaryVisitScheduleItem, PressureAnalysisSet, PressureFlow, Role, StudyDcfSetting, StudyPefSetting, CoughDeadlinesModel, CoughProcessingAlgorithms, AnalysisSetAccess, DevicesSpirotracVersionPatchModel } from "./apiTypes";
import { commonHeaders } from './ClientResources';
import { AnalysisBatchCreateModel, AnalysisBatchDeleteModel } from './SerenityAnalysisBatch';
import { VitaloJak2LogFile } from './DeviceLinkApi/VitaloJak2';
import { CoughSessionMutationModel } from './SerenityCoughSession';
import { DlcoResults, PFTOverreading, TestRepeatabilityResults, TestResults, Parameters, OscillometryResults, LungVolumeResults, StudyRelevantParameters, StudyLungVolumeRelevantParameters, SpirometryResults, Parameter, SvcResults, StudySvcRelevantParameters, PFTResults } from '../../PulmonaryFunctionTests/Shared/PFTAPITypes';
import { MapDlcoTestAcceptabilitySummaries } from './SerenityDlcoSessions';
import { ConfigurationElements } from './SerenityEDiary';
import { MapLungVolumeTestAcceptabilitySummaries } from './SerenityLungVolumeApi';
import { MapOscillometryTestAcceptabilitySummaries } from './SerenityOscillometry';
import { MapFvcTestAcceptabilitySummaries, MapFvcTestAcceptabilitySummariesForAnalysisSet, MapPefTestAcceptabilitySummariesForAnalysisSet } from './SerenitySpirometry';
import { MapSvcTestAcceptabilitySummaries } from './SerenitySvc';
import { SessionTypes } from './ApiSessionTypes';
import { MapMepTestAcceptabilitySummaries } from './Pressure/SerenityMep';
import { MapMipTestAcceptabilitySummaries } from './Pressure/SerenityMip';
import { MapSnipTestAcceptabilitySummaries } from './Pressure/SerenitySnip';
import { NotificationPlugin } from "../../notifications/NotificationTypes";

declare const rootUrl: string;

export class WebPortalLegacyClientInternal {
    private http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
    private baseUrl: string;

    constructor(baseUrl?: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }) {
        this.http = http ? http : window as any;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
    }

    getCurrentUser(): Promise<any> {
        const url_ = `${this.baseUrl}api/users/getcurrentuser`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_)
            .then(response => {
                if (response.status === 401) {
                    return Promise.reject("User unauthorized");
                } else if (response.status === 403) {
                    return Promise.reject("User requires approval");
                } else {
                    return response.json();
                }
            });
    }

    postLogin(): Promise<any> {
        const url_ = `${this.baseUrl}api/users/postLogin`;
        const options_ = {
            method: "POST",
        }

        return this.http.fetch(url_, options_)
            .then(response => {
                if (response.status === 401) {
                    return Promise.reject("User unauthorized");
                } else if (response.status === 403) {
                    return Promise.reject("User requires approval");
                } else {
                    return response.json();
                }
            });
    }

    getCoughProcessingAlgorithms(): Promise<CoughProcessingAlgorithms[]> {
        const url_ = `${this.baseUrl}api/referenceData/coughProcessingAlgorithms`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    } 
    
    getGenders(): Promise<Gender[]> {
        const url_ = `${this.baseUrl}api/referenceData/genders`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getSmokingStatuses(): Promise<SmokingStatus[]> {
        const url_ = `${this.baseUrl}api/referenceData/smokingStatuses`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getParameters(): Promise<ParameterEntity[]> {
        const url_ = `${this.baseUrl}api/referenceData/parameters`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getLanguages(studyId: string): Promise<Language[]> {
        if (studyId !== null || studyId !== undefined || studyId !== "") {
            const url_ = `${this.baseUrl}api/languages/${studyId}`;
            const options_ = {
                method: "GET",
            }

            return this.http.fetch(url_, options_).then(ProcessFetchResponse);
        } else {
            const url_ = `${this.baseUrl}api/ReferenceData/languages`;
            const options_ = {
                method: "GET",
            }

            return this.http.fetch(url_, options_).then(ProcessFetchResponse);
        }
    }

    getTableParameters(): Promise<TableParameters> {
        const url_ = `${this.baseUrl}api/settings/getTableParameters`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyCoughSettings(id?: string): Promise<StudyCoughSetting> {
        const url_ = `${rootUrl}api/settings/studyCoughSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyFvcSettings(id?: string): Promise<StudyFvcSetting> {
        const url_ = `${rootUrl}api/settings/studyFvcSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudySvcSettings(id?: string): Promise<StudySvcSetting> {
        const url_ = `${rootUrl}api/settings/studySvcSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyPulseOximetrySettings(id?: string): Promise<StudyPulseOximetrySetting> {
        const url_ = `${rootUrl}api/settings/studyPulseOximetrySettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyPcfSettings(id?: string): Promise<StudyPcfSetting> {
        const url_ = `${rootUrl}api/settings/studyPcfSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyEcgSettings(id?: string): Promise<StudyEcgSetting> {
        const url_ = `${rootUrl}api/settings/studyEcgSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyDlcoSettings(id?: string): Promise<StudyDlcoSetting> {
        const url_ = `${rootUrl}api/settings/studyDlcoSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyDiarySettings(id?: string): Promise<StudyDiarySetting> {
        const url_ = `${rootUrl}api/settings/studyDiarySettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyMipSettings(id?: string): Promise<StudyPressureSetting> {
        const url_ = `${rootUrl}api/settings/studyMipSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyMepSettings(id?: string): Promise<StudyPressureSetting> {
        const url_ = `${rootUrl}api/settings/studyMepSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudySnipSettings(id?: string): Promise<StudyPressureSetting> {
        const url_ = `${rootUrl}api/settings/studySnipSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyLungVolumeSettings(id?: string): Promise<StudyLungVolumeSetting> {
        const url_ = `${rootUrl}api/settings/StudyLungVolumeSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyPefSettings(id?: string): Promise<StudyPefSetting> {
        const url_ = `${rootUrl}api/settings/studyPefSettings${id ? '/' + id : ''}`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudySubjectSettings(): Promise<StudySubjectSetting> {
        const url_ = `${rootUrl}api/settings/StudySubjectSettings`;
        const options_ = {
            method: "GET",
            headers: {
                'content-type': 'application/json',
            },
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getPermissions(query: Query<Permission>): Promise<Query<Permission>> {
        const url_ = `${rootUrl}api/permissions/current`;
        const options_: RequestInit = {
            method: "POST",
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getStudyLogo(studyId?: string): Promise<Blob> {
        const url_ = `${rootUrl}account/getstudylogo/${studyId}`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(response => response.blob());
    }

    getReportWidgets(): Promise<ReportWidget[]> {
        const url_ = `${rootUrl}api/reports/widgets`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(response => response.json());
    }

    progressWidgetInit() {
        const url_ = `${rootUrl}progresswidget/initialize`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(response => response.json());
    }

    getAdminCore() {
        const url_ = `${rootUrl}admin/admincore`;
        const options_ = {
            method: "GET",
        }

        return this.http.fetch(url_, options_).then(response => response.json());
    }

    getUsers(query: Query<User>): Promise<Query<User>> {
        const url_ = `${rootUrl}api/users`;
        const options_ = {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify(query),
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    getEDiaryConfigurations(query: Query<EDiaryConfiguration>): Promise<Query<EDiaryConfiguration>> {
        const url_ = `${rootUrl}api/eDiaryConfigurations`;
        const options_ = {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify(query),
        }

        return this.http.fetch(url_, options_).then(ProcessFetchResponse);
    }

    createEDiaryConfiguration(model: EDiaryConfiguration): Promise<EDiaryConfiguration> {
        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/create`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(model),
        })
            .then(ProcessFetchResponse);
    }

    patchDevicesSpirotracVersion(model: DevicesSpirotracVersionPatchModel) {
        return this.http.fetch(`${rootUrl}api/device/spirotracversions`, {
            method: 'PATCH',
            ...commonHeaders,
            body: JSON.stringify(model),
        })
        .then(ProcessFetchResponse);
    }

    editEDiaryConfiguration(model: EDiaryConfiguration): Promise<EDiaryConfiguration> {
        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/edit/${model.Id}`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(model),
        })
            .then(ProcessFetchResponse);
    }

    uploadEDiaryConfigurationFiles(configId: string, files: File[]): Promise<EDiaryConfiguration> {
        const form = new FormData();
        for (const file of files) {
            form.append('files', file, file.name);
        }

        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/upload/${configId}`, {
            method: 'POST',
            credentials: 'same-origin',
            body: form,
        })
            .then(ProcessFetchResponse);
    }

    deleteEDiaryConfiguration(id: string): Promise<EDiaryConfiguration> {
        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/delete/${id}`, {
            method: 'DELETE',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    deleteEDiaryConfigurationFile(id: string): Promise<Response> {
        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/deleteFile/${id}`, {
            method: 'DELETE',
            ...commonHeaders,
        });
    }


    downloadEDiaryConfigurationFile(id: string): Promise<Blob> {
        return this.http.fetch(`${rootUrl}api/eDiaryConfigurations/downloadFile/${id}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(response => response.blob())
    }

    getAlertNotifications(query: Query<AlertNotification>): Promise<Query<AlertNotification>> {
        return this.http.fetch(`${rootUrl}api/AlertNotifications`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(response => response.json());
    }

    analysisBatches(query: Query<AnalysisBatch>): Promise<Query<AnalysisBatch>> {
        return this.http.fetch(`${rootUrl}api/analysisbatches`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query)
        })
            .then(ProcessFetchResponse);
    }

    createAnalysisBatch(model: AnalysisBatchCreateModel): Promise<AnalysisBatch> {
        return this.http.fetch(`${rootUrl}api/analysisbatches`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(model),
        })
            .then(ProcessFetchResponse);
    }

    deleteAnalysisBatch(id: string, model: AnalysisBatchDeleteModel): Promise<Response> {
        return this.http.fetch(`${rootUrl}api/analysisbatches/${id}`, {
            method: 'DELETE',
            ...commonHeaders,
            body: JSON.stringify(model),
        });
    }

    getAnalysisSetAuditEntities(query: Query<AuditTrailEntity>): Promise<Query<AuditTrailEntity>> {
        return this.http.fetch(`${rootUrl}api/auditEntities/analysisSet`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(response => response.json());
    }

    getAuditTrailEvents(query: Query<AuditTrailEvent>): Promise<Query<AuditTrailEvent>> {
        return this.http.fetch(`${rootUrl}api/AuditEvents`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getAuditTrailEvent(id: string): Promise<AuditTrailEvent> {
        return this.http.fetch(`${rootUrl}api/AuditEvent/${id}`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            },
        })
            .then(ProcessFetchResponse);
    }

    getAuditTrailActions(query: Query<AuditTrailAction>): Promise<Query<AuditTrailEvent>> {
        return this.http.fetch(`${rootUrl}api/AuditEvents/ActionTypes`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getBloodGlucoseSessions(query: Query<BloodGlucoseSession>): Promise<Query<BloodGlucoseSession>> {
        return this.http.fetch(`${rootUrl}api/BloodGlucoseSessions`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    setSessionToReceived(sessionId: string, uploadTechnicianUser: string): Promise<BloodGlucoseSession> {
        return this.http.fetch(`${rootUrl}api/BloodGlucoseSessions/UpdateStatus`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                id: sessionId,
                uploadTechnicianUser: uploadTechnicianUser,
                statusId: 'BGSS_RECEIVED_DATA',
            })
        })
            .then(ProcessFetchResponse);
    }

    signOffTests(sessionId: string, annotation: string): Promise<BloodGlucoseSession> {
        return this.http.fetch(`${rootUrl}api/BloodGlucoseSessions/SignOffTests`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                sessionId: sessionId,
                annotation: annotation,
            })
        })
            .then(ProcessFetchResponse);
    }

    sendAnalysisSetForReview(anSetExternalCode: string): Promise<CoughAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${anSetExternalCode}/MarkReviewRequired`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({}),
        })
            .then(ProcessFetchResponse);
    }

    completeAnalysisSetReview(anSetExternalCode: string) {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${anSetExternalCode}/CompleteReview`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({}),
        })
            .then(ProcessFetchResponse);
    }

    getCoughAnalysisSets(query: Query<CoughAnalysisSet>): Promise<Query<CoughAnalysisSet>> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse)
    }

    getCoughAnalysisSet(analysisSetId: string): Promise<CoughAnalysisSet> {

        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${analysisSetId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    getCoughAnalysisSetAccess(analysisSetId: string): Promise<AnalysisSetAccess> {

        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${analysisSetId}/access`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    createNewAnalysisSet(sessionId: string, anaylsisSetType: string): Promise<CoughAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify({
                SessionId: sessionId,
                TypeId: anaylsisSetType
            })
        })
            .then(ProcessFetchResponse)
    }

    createNewLinkedAnalysisSet(analysisSetId: string, anaylsisSetType: string): Promise<CoughAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/CreateLinkedAnalysisSet`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                sourceAnalysisSetId: analysisSetId,
                analysisSetType: anaylsisSetType
            })
        })
            .then(ProcessFetchResponse)
    }

    getRecordingLogFile(analysisSetId: string): Promise<VitaloJak2LogFile> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${analysisSetId}/RecordingLogFile`, {
            method: 'GET',
            ...commonHeaders
        })
            .then(ProcessFetchResponse);
    }

    reImportDeviceEvents(analysisSetId: string, events: CoughAnalysisSetEvent[], annotation: string) {
        return this.http.fetch(`${rootUrl}api/AnalysisSets/${analysisSetId}/CoughAnalysisSetEvents/DeviceEvents/ReImport`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify({
                annotation: annotation,
                events: events
            })
        })
            .then(ProcessFetchResponse);
    }

    removeLinkedAnalysisSet(analysisSetId: string, annotation: string): Promise<void> {
        return this.http.fetch(`${rootUrl}api/RemoveLinkedAnalysisSet`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                externalCode: analysisSetId,
                annotation: annotation
            })
        })
            .then(ProcessFetchResponse);
    }

    /**
     *
     * API endpoint for reopening a specific analysis set.
     * @param id
     * @param freeTextEntry
     * @param selectedFixedComments
     */
    reOpenAnalysisSet(id: string, freeTextEntry: string, selectedFixedComments: CoughAnalysisSetReOpenComment[]): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}Api/CoughAnalysisSets(${id})/ReOpen`, {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify({
                selectedReopenComments: selectedFixedComments,
                freeTextEntry: freeTextEntry
            })
        })
            .then(ProcessFetchResponse)
    }

    getReOpenAnalysisSetFixedCommentsForSet(id: string): Promise<CoughReOpenComments> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${id}/AuditComments/ReOpen`, {
            ...commonHeaders,
            method: "GET",
        })
            .then(ProcessFetchResponse);
    }

    getReOpenAnalysisSetFixedComments(): Promise<CoughReOpenComments> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/AuditComments/ReOpen`, {
            ...commonHeaders,
            method: "GET"
        })
            .then(ProcessFetchResponse);
    }

    /**
     *
     * API endpoint for updating the reasons for reopening an anlysis set.
     * @param id
     * @param freeTextEntry
     * @param selectedFixedComments
     */
    updateAnalysisSetReOpenReasons(id: string, freeTextEntry: string, selectedFixedComments: CoughAnalysisSetReOpenComment[]): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}Api/CoughAnalysisSets/${id}/ReOpenComments`, {
            ...commonHeaders,
            method: "PUT",
            body: JSON.stringify({
                selectedReopenComments: selectedFixedComments,
                freeTextEntry: freeTextEntry
            })
        })
            .then(ProcessFetchResponse)
    }

    updateAnalysisDeadlines(id: string, deadlines: CoughDeadlinesModel): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}Api/CoughAnalysisSets/${id}/SetDeadlines`, {
            ...commonHeaders,
            method: "PUT",
            body: JSON.stringify(deadlines)
        })
            .then(ProcessFetchResponse)
    }

    fetchQAAnalysisSetTypes(): Promise<Array<CoughAnalysisSetType>> {
        return this.http.fetch(rootUrl + "api/CoughAnalysisSetTypes", {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify({
                Filters: [
                    {
                        Member: "IsQA",
                        Values: [true]
                    }
                ]
            })
        })
            .then(ProcessFetchResponse)
            .then(json => json.Data);
    }

    getAnalysisSetFixedComments(filters: Array<QueryFilter>): Promise<Query<CoughAnalysisSetFixedComments>> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/FixedComments`, {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify({
                Filters: filters,
            })
        })
            .then(ProcessFetchResponse);
    }

    markAnalysisSetAsReviewComplete(analysisSetId: string, freeTextEntry: string, selectedFixedComments: CoughAnalysisSetReOpenComment[]): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${analysisSetId}/CompleteReview`, {
            method: "POST",
            ...commonHeaders,
            body: JSON.stringify({
                selectedReopenComments: selectedFixedComments,
                freeTextEntry: freeTextEntry
            })
        })
            .then(ProcessFetchResponse);
    }

    updateReviewCompleteComments(analysisSetId: string, freeTextEntry: string, selectedFixedComments: CoughAnalysisSetReOpenComment[]): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${analysisSetId}/AuditComments/Review`, {
            method: "PUT",
            ...commonHeaders,
            body: JSON.stringify({
                selectedReopenComments: selectedFixedComments,
                freeTextEntry: freeTextEntry
            })
        })
            .then(ProcessFetchResponse);
    }

    getReviewCompleteFixedCommentsForSet(id: string): Promise<CoughReOpenComments> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${id}/AuditComments/ReviewComplete`, {
            ...commonHeaders,
            method: "GET",
        })
            .then(ProcessFetchResponse);
    }

    resetCoughAnalysisSetReview(id: string, auditAnnotation: string): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${id}/ResetReview`, {
            ...commonHeaders,
            method: "POST",
            body: JSON.stringify({
                auditAnnotation: auditAnnotation
            })
        })
            .then(ProcessFetchResponse);
    }

    startNewReview(id: string, auditAnnotation: string): Promise<void | ModelWithError> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${id}/StartNewReview`, {
            ...commonHeaders,
            method: "POST",
            body: JSON.stringify({
                auditAnnotation: auditAnnotation
            })
        })
            .then(ProcessFetchResponse);
    }

    createManualQASet(fromAnalysisSetId: string, typeExternalCode: string): Promise<CoughAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${fromAnalysisSetId}/CreateManualQASet`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                fromAnalysisSetId: fromAnalysisSetId,
                typeExternalCode: typeExternalCode
            })
        })
            .then(ProcessFetchResponse)
    }

    updateCreatedFromAnalysisSet(fromAnalysisSetId: string, analysisSetToSwitchToId: string, annotation: string): Promise<CoughAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/${fromAnalysisSetId}/ChangeQACreatedFrom`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify({
                analysisSetToSwitchToId: analysisSetToSwitchToId,
                annotation: annotation
            })
        })
            .then(ProcessFetchResponse)
    }

    getCoughAnalysisSetEvents(query: Query<CoughAnalysisSetEvent>): Promise<Query<CoughAnalysisSetEvent>> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSetEvents`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    putCoughAnalysisSetEvent(event: CoughAnalysisSetEvent): Promise<CoughAnalysisSetEvent> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSetEvents`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(event),
        })
            .then(ProcessFetchResponse);
    }

    putCoughAnalysisSetDeviceEvents(event: CoughAnalysisSetEvent): Promise<CoughAnalysisSetEvent> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSetEvents/DeviceEvents`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(event),
        })
            .then(ProcessFetchResponse);
    }

    getCoughAnalysisSetTypesByFilter(typeIds: string[]): Promise<Query<CoughAnalysisSetType>> {
        return this.http.fetch(rootUrl + "api/coughanalysissettypes", {
            ...commonHeaders,
            method: "POST",
            body: JSON.stringify({
                Filters: [
                    {
                        Member: "Id",
                        Values: typeIds
                    }
                ]
            })
        })
            .then(ProcessFetchResponse)
    }

    getCoughAnalysisSetTypes(): Promise<Query<CoughAnalysisSetType>> {
        return this.http.fetch(rootUrl + "api/coughanalysissettypes", {
            ...commonHeaders,
            method: "POST",
            body: JSON.stringify({
                Filters: [],
            }),
        })
            .then(ProcessFetchResponse)
    }

    getCoughSessions(filters: Array<QueryFilter>): Promise<Query<CoughSession>> {
        return this.http.fetch(`${rootUrl}api/CoughSessions`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                Filters: filters,
            }),
        })
            .then(ProcessFetchResponse)
    }

    checkVitaloJAKCardAvailability(serial: string, studyId: string): Promise<VitaloJakCardAvailablity> {
        return this.http.fetch(`${rootUrl}api/CoughSessions/CheckVitalojakCardAvailability/${serial}/${studyId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse)
    }

    toggleQARequired(sessionId: string, isQARequired: boolean): Promise<CoughSession> {
        return this.http.fetch(`${rootUrl}Api/CoughSessions(${sessionId})/ToggleQARequired/${isQARequired}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse)
    }

    getMediaSerialNumberByExternalCode(externalCode: string): Promise<string> {
        return this.http.fetch(`${rootUrl}api/CoughSessions(${externalCode})/GetMediaSerialNumberByExternalCode`, {
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    createCoughSession(model: CoughSessionMutationModel): Promise<CoughSession> {
        return this.http.fetch(`${rootUrl}api/CoughSessions`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(model),
        })
            .then(response => response.json());
    }

    confirmMailing(sessionId: string, trackingNumber: string): Promise<any> {
        return this.http.fetch(`${rootUrl}api/CoughSessionPostalRecords/ConfirmMailing`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                sessionId: sessionId,
                trackingNumber: trackingNumber,
            })
        })
            .then(ProcessFetchResponse);
    }

    getCoughSessionNotes(sessionExternalCode: string, query: Query<SessionNote>): Promise<Query<SessionNote>> {
        return this.http.fetch(`${rootUrl}api/CoughSessions/${sessionExternalCode}/notes`, {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getStudyDcfSettings(id?: string): Promise<StudyDcfSetting> {
        return this.http.fetch(`${rootUrl}api/Settings/StudyDcfSettings${id ? '/' + id : ''}`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json'
            }
        })
            .then(ProcessFetchResponse);
    }

    deviceAllocations(query: Query<DeviceAllocation>): Promise<Query<DeviceAllocation>> {
        return this.http.fetch(`${rootUrl}api/deviceallocations`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse)
    }

    getAvailableDevicesOfTypeForSite(deviceTypeId: string, siteId: string): Promise<Array<Device>> {
        return this.http.fetch(`${rootUrl}api/deviceallocations/GetAvailableDeviceOfTypeForSite`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                deviceTypeId: deviceTypeId,
                siteId: siteId,
            }),
        })
            .then(ProcessFetchResponse)
    }

    allocateDeviceToSubject(serialNumber: string, subjectId: string): Promise<Array<Device>> {
        return this.http.fetch(`${rootUrl}api/deviceallocations/allocateDeviceToSubject`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                serialNumber: serialNumber,
                subjectId: subjectId,
            }),
        })
            .then(ProcessFetchResponse)
    }

    removeSubjectAllocation(allocationId: string, subjectId: string): Promise<boolean> {
        return this.http.fetch(`${rootUrl}api/deviceallocations/removeSubjectAllocation`, {
            method: 'DELETE',
            ...commonHeaders,
            body: JSON.stringify({
                allocationId: allocationId,
                subjectId: subjectId,
            }),
        })
            .then(response => response.ok)
    }

    deviceTypeInteractions(query: Query<DeviceTypeInteraction>): Promise<Query<DeviceTypeInteraction>> {
        return this.http.fetch(`${rootUrl}api/DeviceTypeInteractions`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query)
        })
            .then(ProcessFetchResponse);
    }

    getDlcoSessions(query: Query<DlcoSessionMetadata>): Promise<Query<DlcoSessionMetadata>> {
        return this.http.fetch(`${rootUrl}api/dlco/SessionMetadata`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                query: query
            })
        })
            .then(ProcessFetchResponse);
    }

    getDlcoAnalysisSets(query: Query<DlcoAnalysisSet>) {
        return this.http.fetch(`${rootUrl}api/dlco/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        })
            .then(ProcessFetchResponse);
    }

    getDlcoSession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/dlco/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getDlcoAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/dlco/AnalysisSets/overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    startOverreading(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/dlco/AnalysisSets/${analysisSetId}/overread`, {
            ...commonHeaders,
            method: 'POST',
        })
            .then(ProcessFetchResponse);
    }

    getDlcoAnalysisSetResults(analysisSetId: string): Promise<DlcoResults> {
        return this.http.fetch(`${rootUrl}api/dlco/AnalysisSets/resultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    updateAcceptabilityDlco(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, auditAnnotation?: string): Promise<DlcoResults> {
        return this.http.fetch(`${rootUrl}api/Dlco/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
            }),
        })
            .then(ProcessFetchResponse)
    }

    getDlcoSessionStatuses(): Promise<OverreadStatus[]> {
        return this.http.fetch(`${rootUrl}api/Dlco/SessionStatuses`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getDlcoAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/Dlco/AnalysisSets/types`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    calculateDlcoBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapDlcoTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Dlco/bestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateDlcoRepeatability(testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapDlcoTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Dlco/repeatability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    reassign(analysisSetId: string, userId: string): Promise<DlcoAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/dlco/analysis/${analysisSetId}/reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    getConfigurationElements(studyId: string): Promise<ConfigurationElements> {
        return this.http.fetch(`${rootUrl}api/EDiary/ConfigurationElements/${studyId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    setSubjectAlarms(subjectId: string, alarms: string[]): Promise<string> {
        return this.http.fetch(`${rootUrl}api/EDiary/SubjectAlarms`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                subjectId: subjectId,
                alarms: alarms,
            })
        })
            .then(ProcessFetchResponse);
    }

    getFiles(filters: Array<QueryFilter>): Promise<Query<File>> {
        return this.http.fetch(`${rootUrl}api/Files`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                Filters: filters,
            }),
        })
            .then(response => response.json());
    }

    getFile(fileAttachmentId: string) {
        return this.http.fetch(`${rootUrl}api/files/${fileAttachmentId}`, {
            method: 'GET'
        });
    }

    postFile(studyId: string, attachmentRelatesTo: number, relatedEntityId: string, file: File): Promise<any> {
        const data = new FormData();
        data.append("files", file, file.name);
        data.append("studyId", studyId);
        data.append("attachmentRelatesTo", attachmentRelatesTo.toString());
        data.append("relatedEntityId", relatedEntityId);

        return this.http.fetch(`${rootUrl}api/files/upload`, {
            method: 'POST',
            body: data,
        })
            .then(response => response.json());
    }

    getGenericSessions(query: Query<Session>): Promise<Query<Session>> {
        return this.http.fetch(`${rootUrl}api/Sessions`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse)
    }

    getSessionStatuses(): Promise<OverreadStatus[]> {
        return this.http.fetch(`${rootUrl}api/SessionStatuses`, {
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getUnconfiguredBySubjectId(subjectId: string): Promise<Array<Session>> {
        return this.http.fetch(`${rootUrl}api/Sessions/GetUnconfiguredBySubjectId/${subjectId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    getVisitIdByExternalCode(externalCode: string): Promise<string> {
        return this.http.fetch(`${rootUrl}api/Sessions(${externalCode})/GetVisitIdByExternalCode`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    getAdjustedUserTime(baseTime: string) {
        return this.http.fetch(rootUrl + 'api/user/getadjustedusertime',
            {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ time: baseTime }),
                credentials: 'same-origin'
            })
            .then(response => response.json());
    }

    getSubjectNotes(query: Query<SubjectNote>): Promise<Query<SubjectNote>> {
        return this.http.fetch(`${rootUrl}api/SubjectNotes`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getSessionNotes(query: Query<SessionNote>): Promise<Query<SessionNote>> {
        return this.http.fetch(`${rootUrl}api/SessionNotes`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    putSubjectNotes(subjectId: string, annotation: string): Promise<SessionNote> {
        return this.http.fetch(`${rootUrl}api/SubjectNotes`, {
            ...commonHeaders,
            method: "PUT",
            body: JSON.stringify({
                SubjectId: subjectId,
                Text: annotation,
                CreatedDate: moment().toISOString(true)
            }),
        })
            .then(ProcessFetchResponse);
    }

    getNotesBySessionId(sessionId: string): Promise<SessionNote[]> {
        return this.http.fetch(`${rootUrl}api/SessionNotes/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    putSessionNotes(sessionId: string, annotation: string): Promise<SessionNote> {
        return this.http.fetch(`${rootUrl}api/SessionNotes`, {
            ...commonHeaders,
            method: "PUT",
            body: JSON.stringify({
                SessionId: sessionId,
                Text: annotation,
                CreatedDate: moment().toISOString(true)
            }),
        })
            .then(ProcessFetchResponse);
    }

    getLungVolumeAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/lungvolume/AnalysisSets/overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    startOverreadingLungVolume(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/lungvolume/AnalysisSets/${analysisSetId}/overread`, {
            ...commonHeaders,
            method: 'POST',
        })
            .then(ProcessFetchResponse);
    }

    updateAcceptabilityLungVolume(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, auditAnnotation?: string): Promise<LungVolumeResults> {
        return this.http.fetch(`${rootUrl}api/lungvolume/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
            }),
        })
            .then(ProcessFetchResponse)
    }

    calculateLungVolumeHeBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapLungVolumeTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/lungvolume/bestTestsHe`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateLungVolumePlethBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapLungVolumeTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/lungvolume/bestTestsPleth`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateLungVolumeRepeatability(sessionId: string, testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapLungVolumeTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/lungvolume/repeatability/${sessionId}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getLungVolumeSession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/lungvolume/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getLungVolumeAnalysisSetResults(analysisSetId: string): Promise<LungVolumeResults> {
        return this.http.fetch(`${rootUrl}api/lungvolume/AnalysisSets/resultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getLungVolumeAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/lungvolume/AnalysisSets/types`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }
    
    reassignLungVolume(analysisSetId: string, userId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/LungVolume/AnalysisSets/${analysisSetId}/Reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    getOscillometryAnalysisSets(query: Query<OscillometryAnalysisSet>) {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        }).then(ProcessFetchResponse);
    }

    getOscillometrySession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getOscillometryAnalysisSetTypes(): Promise<OscillometryAnalysisSetTypeModel[]> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets/Types`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getOscillometryAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets/Overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    startOscillometryOverreading(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets/${analysisSetId}/Overread`, {
            ...commonHeaders,
            method: 'POST',
        }).then(ProcessFetchResponse);
    }

    getOscillometryAnalysisSetResults(analysisSetId: string): Promise<OscillometryResults> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets/ResultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    calculateOscillometryBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapOscillometryTestAcceptabilitySummaries(testResults);
        return this.http.fetch(`${rootUrl}api/Oscillometry/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    reassignOscillometry(analysisSetId: string, userId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/Analysis/${analysisSetId}/Reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    updateAcceptabilityOscillometry(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, auditAnnotation?: string): Promise<OscillometryResults> {
        return this.http.fetch(`${rootUrl}api/Oscillometry/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
            }),
        })
            .then(ProcessFetchResponse)
    }

    calculateOscillometryRepeatability(testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapOscillometryTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Oscillometry/repeatability/`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    createOscillometryQC(sessionId: string, typeId: string, userId?: string): Promise<OscillometryAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/oscillometryQC/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    getFixedComments(query: Query<OverreadFixedComment>): Promise<Query<OverreadFixedComment>> {
        return this.http.fetch(`${rootUrl}api/overreadfixedcomments`, {
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            body: JSON.stringify(query),
        }).then(ProcessFetchResponse);
    }

    getFixedCommentSets(query: Query<OverreadFixedCommentSet>): Promise<Query<OverreadFixedCommentSet>> {
        return this.http.fetch(`${rootUrl}api/overreadfixedcommentsets`, {
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            body: JSON.stringify(query)
        }).then(response => response.json());
    }

    async getFixedCommentSet(setId: string): Promise<OverreadFixedCommentSet> {
        return (await this.getFixedCommentSets({
            Filters: [
                {
                    Member: "Id",
                    Values: [setId]
                }
            ]
        })).Data[0];
    }

    getParametersBySessionType(sessionType: string): Promise<ParameterEntity[]> {
        return this.http.fetch(`${rootUrl}api/ReferenceData/parameters/${sessionType}`, {
            ...commonHeaders,
            method: 'GET'
        })
            .then(ProcessFetchResponse);
    }

    getReports = (query?: Query<Report>): Promise<Query<Report>> => {
        return this.http.fetch(`${rootUrl}api/reports`, {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query)
        })
            .then(response => response.json())
    }

    /**
     *Getting the guid of the folder that is selected for a study.
     * */
    getStudySelectedRootFolder(): Promise<string> {
        return this.http.fetch(`${rootUrl}api/reports/folders/selected`, { credentials: "same-origin" })
            .then(ProcessFetchResponse);
    }

    getFolders(server: string, username: string, password: string = null, passwordEncrypted: string = null): Promise<Query<ReportingServicesCatalogItem>> {
        return this.http.fetch(`${rootUrl}api/reports/folders`, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                credentials: { serverURL: server, username: username, password: password, passwordEncrypted: passwordEncrypted },
                query: {
                    Order: { Member: "Name", Direction: "Ascending" },
                },
            })
        })
            .then(response => response.json())

    }

    testReportServerConnection = (server: string, username: string, password: string): Promise<boolean> => {
        return this.http.fetch(`${rootUrl}api/Reports/TestConnection`, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({ server: server, username: username, password: password }),
        })
            .then(ProcessFetchResponse).catch(() => false);
    }

    testStoredConnection = (): Promise<boolean> => {
        return this.http.fetch(rootUrl + 'api/reports/teststoredconnection', {
            credentials: 'same-origin',
        })
            .then(ProcessFetchResponse).catch(() => false);
    }

    getBaseReportsMetadata = (server: string, username: string, password: string): Promise<Array<ReportingServicesCatalogItem>> => {
        return this.http.fetch(`${rootUrl}api/reports/GetBaseReportsMetadata`, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                server: server,
                username: username,
                password: password
            })
        })
            .then(response => response.json())
    }

    getReportParameters = (reportId: string): Promise<any> => {
        return this.http.fetch(rootUrl + `api/reports/reportparameters/${reportId}`)
            .then(response => response.json());
    }

    importBaseReports = (server: string, username: string, password: string, folderToImportInto: string, connectionString: string): Promise<Array<ImportSummaryResult> | ModelWithError> => {
        return this.http.fetch(`${rootUrl}api/reports/ImportBaseReports`, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                server: server,
                username: username,
                password: password,
                folderToImportInto: folderToImportInto,
                connectionString: connectionString,
            })
        }).then(response => response.json())
    }

    getSettings(filters: Array<QueryFilter>): Promise<Query<Setting>> {
        return this.http.fetch(`${rootUrl}api/Settings`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                Filters: filters,
            }),
        })
            .then(response => response.json())
    }

    getSettingsHelper(key: string, studyId: string): Promise<Query<Setting>> {
        const filters: Array<QueryFilter> = [
            { Member: 'Key', Values: [key] },
            { Member: 'StudyId', Values: [studyId] }
        ];

        return this.getSettings(filters);
    }

    getStudyFvcRelevantParameters(): Promise<StudyRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudyFvcRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    getStudyMipRelevantParameters(): Promise<StudyRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudyMipRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    getStudyMepRelevantParameters(): Promise<StudyRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudyMepRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    getStudySnipRelevantParameters(): Promise<StudyRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudySnipRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    getStudyLungVolumeRelevantParameters(): Promise<StudyLungVolumeRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudyLungVolumeRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    getSetting(settingKey: string): Promise<Setting<any>> {
        return this.http.fetch(rootUrl + 'api/GetSettings', {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                key: settingKey,
            })
        })
            .then(ProcessFetchResponse);
    }

    getStudySetting(studyId: string, settingKey: string): Promise<Setting<any>> {
        return this.http.fetch(rootUrl + 'api/GetSettings', {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                studyId: studyId,
                key: settingKey,
            })
        })
            .then(ProcessFetchResponse);
    }

    putStudySetting(studyId: string, settingKey: string, settingValue: any): Promise<boolean> {
        return this.http.fetch(rootUrl + 'api/PutStudySettings', {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                setting: {
                    Key: settingKey,
                    Value: typeof settingValue === 'string' ? settingValue : JSON.stringify(settingValue),
                },
                studyId: studyId,
            })
        })
            .then(ProcessCodedExceptionResponse);
    }

    putStudySettingArray(studyId: string, settingKey: string, settingValue: any[]): Promise<boolean> {
        return this.http.fetch(rootUrl + 'api/PutStudySettings', {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                setting: {
                    Key: settingKey,
                    Value: JSON.stringify(settingValue),
                },
                studyId: studyId,
            })
        })
            .then(ProcessCodedExceptionResponse);
    }

    putSetting(settingKey: string, settingValue: any): Promise<any> {
        return this.http.fetch(rootUrl + 'api/putSettings', {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                Key: settingKey,
                Value: typeof settingValue === 'string' ? settingValue : JSON.stringify(settingValue),
            })
        })
            .then(ProcessCodedExceptionResponse);
    }

    getSites(query: Query<Site>): Promise<Query<Site>> {
        return this.http.fetch(`${rootUrl}api/Sites`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(response => response.json());
    }

    getSpirometryAnalysisSets(query: Query<SpirometryAnalysisSet>): Promise<Query<SpirometryAnalysisSet>> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        })
            .then(ProcessFetchResponse);
    }

    getSpirometryResultData(analysisSetId: string): Promise<SpirometryResults> {
        return this.http.fetch(`${rootUrl}api/spirometry/AnalysisSets/resultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/spirometry/AnalysisSets/overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getSessionMetadata(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/Spirometry/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    /**
     * Getting all of the spirometry sessions based on the query.
     * @param query
     */
    getSpirometrySessions(query: Query<SessionMetadata>): Promise<Query<SpirometrySessionMetadata>> {
        return this.http.fetch(`${rootUrl}api/Spirometry/SessionMetadata`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                query: query
            })
        })
            .then(ProcessFetchResponse);
    }

    getSpirometryAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets/types`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    getTechnicians(): Promise<string[]> {
        return this.http.fetch(`${rootUrl}api/Spirometry/Technicians`, {
            ...commonHeaders,
            method: 'GET'
        })
            .then(ProcessFetchResponse);
    }

    updateAcceptabilityFvc(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, analysisSet: FvcAnalysisSet, session: FvcSession, auditAnnotation?: string): Promise<SpirometryResults> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet, session),
            }),
        })
            .then(ProcessFetchResponse)
    }

    updateAcceptabilityPef(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, analysisSet: PefAnalysisSet, siteAnalysisSet: PefAnalysisSet, session: PefSession, auditAnnotation?: string): Promise<SpirometryResults> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets/UpdateAcceptabilityPef`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapPefTestAcceptabilitySummariesForAnalysisSet(analysisSet, siteAnalysisSet, session),
            }),
        })
            .then(ProcessFetchResponse)
    }

    startSpirometryOverread(analysisSetGuid: string): Promise<SpirometryAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets/overread/${analysisSetGuid}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({})
        })
            .then(ProcessFetchResponse)
    }

    reassignFvc(analysisSetId: string, userId: string): Promise<SpirometryAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/spirometry/analysis/${analysisSetId}/reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    calculateFvcBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapFvcTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Spirometry/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateFvcBestTestsForAnalysisSet(analysisSet: FvcAnalysisSet, session: FvcSession): Promise<Parameters> {
        const testSummaries = MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet, session);

        return this.http.fetch(`${rootUrl}api/Spirometry/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }
    calculateReversibilityChange(analysisSet: FvcAnalysisSet, session: FvcSession): Promise<Parameters> {
        const testSummaries = MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet, session);

        return this.http.fetch(`${rootUrl}api/Spirometry/${session.id}/ReversibilityChange`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }



    calculatePefBestTestsForAnalysisSet(analysisSet: PefAnalysisSet, siteAnalysisSet: PefAnalysisSet, session: PefSession): Promise<Parameters> {
        const testSummaries = MapPefTestAcceptabilitySummariesForAnalysisSet(analysisSet, siteAnalysisSet, session);

        return this.http.fetch(`${rootUrl}api/Spirometry/BestTestsPef`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateFvcRepeatability(testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapFvcTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Spirometry/Repeatability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateFvcRepeatabilityForAnalysisSet(analysisSet: FvcAnalysisSet, session: FvcSession): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet, session);

        return this.http.fetch(`${rootUrl}api/Spirometry/Repeatability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculatePefRepeatabilityForAnalysisSet(analysisSet: PefAnalysisSet, session: PefSession): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapFvcTestAcceptabilitySummariesForAnalysisSet(analysisSet, session);

        return this.http.fetch(`${rootUrl}api/Spirometry/RepeatabilityPef`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getOverreadChallengeStatuses(query: Query<OverreadChallengeStatus>): Promise<Query<OverreadChallengeStatus>> {
        return this.http.fetch(`${rootUrl}api/Spirometry/OverreadChallengeStatuses`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                query: query
            })
        })
            .then(ProcessFetchResponse);
    }

    getSubjects(query: Query<Subject>): Promise<Query<Subject>> {
        return this.http.fetch(`${rootUrl}api/Subjects`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getSubject(subjectId: string): Promise<Subject> {
        return this.http.fetch(`${rootUrl}api/Subjects/${subjectId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse);
    }

    createSubject(subject: Subject): Promise<Subject> {
        return this.http.fetch(`${rootUrl}api/Subjects`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify(subject)
        })
            .then(ProcessFetchResponse);
    }

    upsertSubject(subjectId: string, subject: Subject, auditAnnotation: string): Promise<Subject> {
        return this.http.fetch(`${rootUrl}api/Subjects/${subjectId}`, {
            method: 'PUT',
            ...commonHeaders,
            body: JSON.stringify({
                model: subject,
                auditAnnotation: auditAnnotation
            })
        })
            .then(ProcessFetchResponse);
    }

    getCalculateBmi(height: number, weight: number): Promise<number> {
        return this.http.fetch(`${rootUrl}api/Subjects/CalculateBmi/${height}/${weight}`, {
            method: 'GET',
            ...commonHeaders
        })
            .then(ProcessFetchResponse);
    }
    getCalculateAge(dateOfBirth: string): Promise<number> {
        return this.http.fetch(`${rootUrl}api/Subjects/CalculateAge/${dateOfBirth}`, {
            method: 'GET',
            ...commonHeaders
        })
            .then(ProcessFetchResponse);
    }

    revealPin(subjectId: string, annotation: string): Promise<string> {
        return this.http.fetch(`${rootUrl}api/Subjects/RevealPin`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                id: subjectId,
                annotation: annotation,
            })
        })
            .then(ProcessFetchResponse);
    }

    createFvcQC(sessionId: string, typeId: string, userId?: string): Promise<SpirometryAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/spirometryQC/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    getFvcParameters(): Promise<Parameter[]> {
        return this.http.fetch(`${rootUrl}api/Studies/Parameters/Fvc`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(response => response.json())
    }

    setFvcTestingConfiguration(studyId: string, fvcSettings: StudyFvcSetting, auditAnnotation: string): Promise<any[]> {
        return this.http.fetch(`${rootUrl}api/Studies/SetTestingConfiguration/Fvc/${studyId}`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                model: fvcSettings,
                auditAnnotation: auditAnnotation,
            }),
        })
            .then(response => response.json())
    }

    getStudyCoughEventTypes(query: Query<StudyCoughEventType>): Promise<Query<StudyCoughEventType>> {
        return this.http.fetch(`${rootUrl}api/StudyCoughEventTypes`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(response => response.json())
    }

    getStudyDeviceTypes(studyId): Promise<Array<DeviceType>> {
        return this.http.fetch(`${rootUrl}api/StudyDeviceTypes/${studyId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse)
    }

    getStudyComments(query: Query<StudyOverreadFixedCommentSet>): Promise<Query<StudyOverreadFixedCommentSet>> {
        return this.http.fetch(`${rootUrl}api/studyoverreadfixedcommentsets`, {
            method: "POST",
            headers: {
                "content-type": "application/json",
            },
            body: JSON.stringify(query)
        }).then(response => response.json());
    }

    getStudyPopulationGroupByStudy(studyId): Promise<Array<StudyPopulationGroup>> {
        return this.http.fetch(`${rootUrl}api/StudyPopulationGroupsByStudyId/${studyId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse)
    }

    getStudySessionTypes(studyId: string): Promise<Array<SessionType>> {
        return this.http.fetch(`${rootUrl}api/StudySessionTypes/${studyId}`, {
            method: 'GET',
            ...commonHeaders,
        })
            .then(ProcessFetchResponse)
    }

    getSvcAnalysisSets(query: Query<SvcAnalysisSet>): Promise<Query<SvcAnalysisSet>> {
        return this.http.fetch(`${rootUrl}api/svcparameterresults/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        })
            .then(ProcessFetchResponse);
    }

    getSvcResultData(analysisSetId: string): Promise<SvcResults> {
        return this.http.fetch(`${rootUrl}api/svcparameterresults/AnalysisSets/resultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    startSvcOverread(analysisSetGuid: string): Promise<void> {
        return this.http.fetch(`${rootUrl}api/svcparameterresults/AnalysisSets/overread/${analysisSetGuid}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({})
        })
            .then(ProcessFetchResponse);
    }

    getStudySvcRelevantParameters(): Promise<StudySvcRelevantParameters> {
        return this.http.fetch(`${rootUrl}api/Settings/StudySvcRelevantParameters`, {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            }
        })
            .then(ProcessFetchResponse);
    }

    calculateSvcBestTests(testResults: TestResults[]): Promise<Parameters> {

        const testSummaries = MapSvcTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/svcparameterresults/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getSvcAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/svcparameterresults/AnalysisSets/overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    calculateSvcRepeatability(testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {

        const testSummaries = MapSvcTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/svcparameterresults/Repeatability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    updateAcceptabilitySvc(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, testResults: TestResults[], auditAnnotation?: string): Promise<SvcResults> {
        return this.http.fetch(`${rootUrl}api/svcparameterresults/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapSvcTestAcceptabilitySummaries(testResults),
            }),
        })
            .then(ProcessFetchResponse)
    }

    createSvcQC(sessionId: string, typeId: string, userId?: string): Promise<SvcAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/svcQC/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    getUsersUnrestricted(query: Query<User>): Promise<Query<User>> {
        return this.http.fetch(`${rootUrl}api/Users/UnRestricted`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse);
    }

    getUsersByPermission(studyId: string, permissionId: string, excludeDisabled = false): Promise<User[]> {
        return this.http.fetch(`${rootUrl}api/Users/GetUsersByPermission`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                studyId: studyId,
                permissionId: permissionId,
                excludeDisabled: excludeDisabled
            }),
        })
            .then(ProcessFetchResponse);
    }

    getUsersByPermissionIncludingAllStudies(studyId: string, permissionId: string): Promise<User[]> {
        return this.http.fetch(`${rootUrl}api/Users/GetUsersByPermissionIncludingAllStudies`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                studyId: studyId,
                permissionId: permissionId,
            }),
        })
            .then(ProcessFetchResponse);
    }

    assignToUser(analysisSetId: string, userId: string): Promise<any> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/UpdateAssignedAnalyst`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                id: analysisSetId,
                userId: userId,
            }),
        })
            .then(response => response.json());
    }

    assignToVisualReviewAnalyst(analysisSetId: string, userId: string): Promise<any> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/UpdateAssignedVisualReviewAnalyst`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                id: analysisSetId,
                userId: userId,
            }),
        })
            .then(response => response.json());
    }

    updateVisualReviewStatus(analysisSetId: string): Promise<any> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/UpdateVisualReviewStatus`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                id: analysisSetId
            }),
        })
            .then(response => response.json());
    }

    updateVisualReviewAnalystSelectionStatus(analysisSetId: string, statusValue: number): Promise<any> {
        return this.http.fetch(`${rootUrl}api/CoughAnalysisSets/UpdateVisualReviewAnalystSelectionStatus`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                id: analysisSetId,
                visualInspectionSelectionId: statusValue
            }),
        })
            .then(response => response.json());
    }


    getVisitSchedule(query: Query<VisitScheduleItem>): Promise<Query<VisitScheduleItem>> {
        return this.http.fetch(`${rootUrl}api/VisitSchedule`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse)
    }

    getSessionSchedule(query: Query<SessionScheduleItem>): Promise<Query<SessionScheduleItem>> {
        return this.http.fetch(`${rootUrl}api/SessionSchedule`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(query),
        })
            .then(ProcessFetchResponse)
    }

    getDiarySessionScheduleItemAsync(id: string): Promise<EDiarySessionScheduleItem> {
        return this.http.fetch(`${rootUrl}api/DiarySessionScheduleItem/${id}`, {
            method: 'GET',
        })
            .then(ProcessFetchResponse)
    }

    getAvailableSubjectVisits(subjectId: string, sessionType: SessionTypes): Promise<Array<StudyScheduleVisit>> {
        return this.http.fetch(`${rootUrl}api/VisitSchedule/AvailableSubjectVisits/${subjectId}/${sessionType}`, {
            method: 'GET',
        })
            .then(ProcessFetchResponse)
            .then(json => json.map(v => ({  // This maps to the new /api/v3 structure as a adapter pattern until the server action can be converted over
                id: v.Id,
                label: v.Label,
                description: v.Description,
                spanDays: v.CanSpanDays,
                commentRequired: v.CommentRequired,
                confirmationRequired: v.ConfirmationRequired,
                mandatory: v.IsMandatory,
                disabled: v.Disabled,
                phaseId: v.Phase.Id,
                studyScheduleSessions: v.Sessions.map(s => ({
                    id: s.Id,
                    label: s.Label,
                    rules: s.Rules,
                    description: s.Description,
                    sessionTypeId: s.SessionTypeId,
                    deviceTypeId: s.DeviceTypeId,
                    studyScheduleVisitId: s.VisitScheduleItemId,
                    ordinal: s.DisplayOrder,
                    mandatory: s.IsMandatory,
                    disabled: s.Disabled,
                    preStudyScheduleSessionId: s.PreSessionItemId,
                    afterRandomisation: s.AfterRandomisation,
                })),
                ordinal: v.DisplayOrder,
            })))
    }

    editVisitSchedule(studyId: string, visitScheduleItems: Array<EDiaryVisitScheduleItem>, auditAnnotation: string): Promise<Array<VisitScheduleItem>> {
        return this.http.fetch(`${rootUrl}api/VisitSchedule/Edit/${studyId}`, {
            method: 'POST',
            ...commonHeaders,
            body: JSON.stringify({
                studyId: studyId,
                visitScheduleItems: visitScheduleItems,
                auditAnnotation: auditAnnotation,
            })
        })
            .then(ProcessFetchResponse)
    }

    getPhases(): Promise<Array<Phase>> {
        return this.http.fetch(`${rootUrl}api/VisitSchedule/Phases`, {
            method: 'GET',
        })
            .then(ProcessFetchResponse)
    }

    getMepAnalysisSets(query: Query<MepAnalysisSet>) {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        }).then(ProcessFetchResponse);
    }

    getMepSession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/Mep/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getMepAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/Types`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getMepAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/Overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    startMepOverreading(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/${analysisSetId}/Overread`, {
            ...commonHeaders,
            method: 'POST',
        }).then(ProcessFetchResponse);
    }

    updateMepAcceptability(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, testResults: TestResults[], auditAnnotation?: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapMepTestAcceptabilitySummaries(testResults),
            }),
        }).then(ProcessFetchResponse)
    }

    getMepAnalysisSetResults(analysisSetId: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/ResultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    calculateMepRepeatability(sessionId: string, testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapMepTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Mep/Repeatability/${sessionId}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    createMepQC(sessionId: string, typeId: string, userId?: string): Promise<PressureAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/mepQc/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    calculateMepBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapMepTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Mep/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getMepFlowData(analysisSetId: string): Promise<PressureFlow> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/PressureData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    reassignMep(analysisSetId: string, userId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Mep/AnalysisSets/${analysisSetId}/Reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    getMipAnalysisSets(query: Query<MipAnalysisSet>) {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        }).then(ProcessFetchResponse);
    }

    getMipSession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/Mip/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getMipAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/Types`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    startMipOverreading(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/${analysisSetId}/Overread`, {
            ...commonHeaders,
            method: 'POST',
        }).then(ProcessFetchResponse);
    }

    updateMipAcceptability(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, testResults: TestResults[], auditAnnotation?: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapMipTestAcceptabilitySummaries(testResults),
            }),
        }).then(ProcessFetchResponse)
    }

    getMipAnalysisSetResults(analysisSetId: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/ResultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    calculateMipRepeatability(sessionId: string, testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapMipTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Mip/Repeatability/${sessionId}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateMipBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapMipTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Mip/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getMipAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getMipFlowData(analysisSetId: string): Promise<PressureFlow> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/PressureData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    reassignMip(analysisSetId: string, userId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Mip/AnalysisSets/${analysisSetId}/Reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    createMipQC(sessionId: string, typeId: string, userId?: string): Promise<PressureAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/mipQc/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    getSnipAnalysisSets(query: Query<SnipAnalysisSet>) {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(query)
        }).then(ProcessFetchResponse);
    }

    getSnipSession(sessionId: string): Promise<SessionMetadata> {
        return this.http.fetch(`${rootUrl}api/Snip/SessionMetadata/${sessionId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getSnipAnalysisSetTypes(): Promise<AnalysisSetType[]> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/Types`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    getSnipAnalysisSetOverreading(analysisSetId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/Overreading/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    startSnipOverreading(analysisSetId: string) {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/${analysisSetId}/Overread`, {
            ...commonHeaders,
            method: 'POST',
        }).then(ProcessFetchResponse);
    }

    updateSnipAcceptability(id: string, status: OverreadStatusEnum, testAcceptabilityMutations: Array<TestAcceptabilityMutation>, sessionFixedComments: Array<FixedCommentMutation>, testFixedComments: Array<any>, testResults: TestResults[], auditAnnotation?: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/UpdateAcceptability`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                id: id,
                acceptabilityMutationModel: {
                    status: status,
                    testAcceptabilityMutations: testAcceptabilityMutations,
                    sessionFixedComments: sessionFixedComments,
                    testFixedComments: testFixedComments,
                    auditAnnotation: auditAnnotation,
                },
                testAcceptabilitySummaries: MapSnipTestAcceptabilitySummaries(testResults),
            }),
        }).then(ProcessFetchResponse)
    }

    getSnipAnalysisSetResults(analysisSetId: string): Promise<PFTResults> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/ResultData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    calculateSnipRepeatability(sessionId: string, testResults: TestResults[]): Promise<TestRepeatabilityResults[]> {
        const testSummaries = MapSnipTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Snip/Repeatability/${sessionId}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    calculateSnipBestTests(testResults: TestResults[]): Promise<Parameters> {
        const testSummaries = MapSnipTestAcceptabilitySummaries(testResults);

        return this.http.fetch(`${rootUrl}api/Snip/BestTests`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify(testSummaries),
        }).then(ProcessFetchResponse);
    }

    getSnipFlowData(analysisSetId: string): Promise<PressureFlow> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/PressureData/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        }).then(ProcessFetchResponse);
    }

    reassignSnip(analysisSetId: string, userId: string): Promise<PFTOverreading> {
        return this.http.fetch(`${rootUrl}api/Snip/AnalysisSets/${analysisSetId}/Reassign`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                analystId: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    createSnipQC(sessionId: string, typeId: string, userId?: string): Promise<PressureAnalysisSet> {
        return this.http.fetch(`${rootUrl}api/snipQc/Create`, {
            ...commonHeaders,
            method: 'PUT',
            body: JSON.stringify({
                sessionId: sessionId,
                typeId: typeId,
                userToAssign: userId,
            }),
        }).then(ProcessFetchResponse);
    }

    getReportCredentials() {
        return this.http.fetch(rootUrl + 'api/reports/Credentials', {
            method: 'GET',
            credentials: 'same-origin',
        })
            .then(response => response.json())
    }

    getSSRSReportRoles(reportId: string, roles: Array<any>) {
        return this.http.fetch(`${rootUrl}api/SSRSReportRoles`, {
            credentials: 'same-origin',
            method: 'PUT',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                ReportId: reportId,
                Roles: roles,
            })
        })
            .then(response => response.json())
    }

    createReportSession() {
        return this.http.fetch(`${rootUrl}api/reports/createsession`, {
            method: "POST",
            credentials: "include",
            headers: {
                "content-type": "application/json"
            }
        });
    }

    getDefinedReportParameters(stateId: string) {
        return this.http.fetch(`${rootUrl}api/definedreports/${stateId}/parameters`)
            .then(response => response.json());
    }

    getDefinedReports(nameFilterQuery: Query<any>) {
        return this.http.fetch(rootUrl + 'api/definedreports', {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify(nameFilterQuery)
        })
            .then(response => response.json())
    }

    getRoles(query: Query<Role>): Promise<Query<Role>> {
        return this.http.fetch(`${rootUrl}api/roles`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({
                query: query
            })
        })
            .then(ProcessFetchResponse);
    }

    importPermissionsApi(roleId: string, data: File): Promise<any> {
        const formData = new FormData();
        formData.append("roleId", roleId);
        formData.append("file", data);

        return this.http.fetch(`${rootUrl}api/rolemanagement/importpermissions`, {
            method: 'POST',
            body: formData,
        })
            .then(ProcessFetchResponse);
    }

    validateAuditPassword(password: string) {
        return this.http.fetch(`${rootUrl}api/users/validatePassword`, {
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({ password: password }),
        })
            .then(response => response.json());
    }

    addDefinedReport(report: any): Promise<any> {
        return this.http.fetch(rootUrl + 'api/definedreports', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(report),
        })
            .then(response => response.json());
    }

    deleteDefinedReport(reportId: string): Promise<void> {
        return this.http.fetch(rootUrl + `api/definedreports/${reportId}`, {
            method: 'DELETE'
        })
            .then(response => null);
    }

    getHubStudyDeviceTypes(studyId: string) {
        return this.http.fetch(`${rootUrl}deviceHub/studyDeviceTypes/${studyId}`,
            {
                credentials: 'same-origin'
            })
            .then(response => response.json())
    }

    getStudyDeviceInstructions(selectedDeviceId: string) {
        return this.http.fetch(`${rootUrl}/DeviceHub/StudyDeviceInstructions/${selectedDeviceId}`,
            {
                credentials: 'same-origin'
            })
            .then(response => response.json())
    }

    logFileDownload(id: string) {
        return this.http.fetch(`${rootUrl}support/logfiledownload/${id}`)
            .then(response => response.json())
    }

    downloadFile(id: string) {
        return this.http.fetch(`${rootUrl}support/downloadfile/${id}`)
            .then(response => response.blob())
    }

    uploadFinalize(sessionId: string) {
        return this.http.fetch(`${rootUrl}api/uploadfinalize?id=${sessionId}`)
            .then(response => response.ok);
    }

    updateAssignedAnalyst(setId: string) {
        return this.http.fetch(rootUrl + "api/CoughAnalysisSets/UpdateAssignedAnalyst", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({
                id: setId
            })
        })
            .then(response => response.json());
    }

    updateAnalysisStartDate(setId: string) {
        return this.http.fetch(rootUrl + "api/CoughAnalysisSets/UpdateAnalysisStartDate", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({
                id: setId
            })
        })
            .then(response => response.json());
    }

    updateCoughAcceptability(analysisSetId, status, selectedWaveform, auditAnnotation) {
        return this.http.fetch(rootUrl + "api/CoughAnalysisSets/UpdateAcceptability", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({
                id: analysisSetId,
                status,
                waveformId: selectedWaveform,
                auditAnnotation: auditAnnotation
            })
        })
            .then(ProcessFetchResponse);
    }

    toggleFixedComment(analysisSetId: string, commentId: string, checked: boolean) {
        return this.http.fetch(rootUrl + "api/CoughAnalysisSets/ToggleFixedComment", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({
                analysisSetId,
                fixedCommentId: commentId,
                isSelected: checked
            })
        })
            .then(response => response.json());
    }

    deleteCoughEvent(removeEvent) {
        return this.http.fetch(rootUrl + "api/coughanalysissetevents", {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
            },
            credentials: "same-origin",
            body: JSON.stringify(removeEvent)
        });
    }

    getEventsForSession(coughSessionId: string) {
        return this.http.fetch(`${rootUrl}Api/CoughSessions/${coughSessionId}/AnalysisSetsEvents`, {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'content-type': 'application/json'
            }
        })
            .then(response => response.json())
    }

    studyMediaPostalAddress(sessionId: string) {
        return this.http.fetch(`${rootUrl}DeviceHub/StudyMediaPostalAddress`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                assessmentGuid: sessionId,
            })
        })
            .then(response => response.json())
    }

    changeWaveform(sessionId: string, currentAnalysisSetId: string, isViewOnly: boolean, waveform: string
    ) {
        return this.http.fetch(rootUrl + "api/coughsessions/updateselectedwaveform", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({
                id: sessionId,
                currentAnalysisSetId,
                isViewOnly,
                waveform
            })
        });
    }

    reprocessWaveforms(coughSessionId: string, auditAnnotation: string) {
        return this.http.fetch(`${rootUrl}api/coughSessions/${coughSessionId}/reprocessWaveforms`, {
            credentials: 'same-origin',
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                sessionExternalCode: coughSessionId,
                auditAnnotation: auditAnnotation
            })
        })
            .then(response => response.json());
    }

    checkDirectory(form) {
        return this.http.fetch(`${rootUrl}api/directoryvisualizer/checkdirectory`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(form)
        })
            .then(response => response.json());
    }

    removeDirectory(directoryName: string) {
        return this.http.fetch(`${rootUrl}api/directoryvisualizer/removedirectory/${directoryName}`, {
            credentials: 'same-origin',
        })
            .then(response => response.json());
    }

    downloadSharedFile(filename: string) {
        return this.http.fetch(`${rootUrl}api/directoryvisualizer/downloadfile`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ filename: filename })
        });
    }

    getNotificationPlugins(): Promise<NotificationPlugin[]> {
        return this.http.fetch(`${rootUrl}api/notificationplugins`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(response => response.json());
    }

    getNotificationSettings(): Promise<any> {
        return this.http.fetch(rootUrl + 'api/Settings/NotificationSettings', {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            },
        })
            .then(ProcessFetchResponse);
    }

    getCalculationPlugins(): Promise<any> {
        return this.http.fetch(rootUrl + 'api/user/calculationplugins', {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            },
        })
            .then(ProcessFetchResponse);
    }

    getCoughUploadReport(id: string): Promise<any> {
        return this.http.fetch(rootUrl + `api/coughreports/archive/${id}/view`, {
            credentials: 'same-origin',
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            },
        })
            .then(response => response.blob());
    }

    startPefOverread(analysisSetGuid: string): Promise<void> {
        return this.http.fetch(`${rootUrl}api/Spirometry/AnalysisSets/overreadPef/${analysisSetGuid}`, {
            ...commonHeaders,
            method: 'POST',
            body: JSON.stringify({})
        })
            .then(ProcessFetchResponse);
    }

    getPefResultData(analysisSetId: string): Promise<SpirometryResults> {
        return this.http.fetch(`${rootUrl}api/spirometry/AnalysisSets/resultDataPef/${analysisSetId}`, {
            ...commonHeaders,
            method: 'GET',
        })
            .then(ProcessFetchResponse);
    }

    LogSignInAuditTrail(signOut: boolean, annotation = "") {
        let url = `${rootUrl}api/Users/LogAuditEvent?signOut=${signOut}`;
        if (annotation) {
            url += `&annotation=${annotation}`;
        }
        return this.http.fetch(url, {
            ...commonHeaders,
            method: 'GET',
        }).then(response => response.ok);
    }
}

export const GetTokenManually = () => {
    for (let i = 0; i < window.sessionStorage.length; i++) {
        if (window.sessionStorage.key(i).includes('oidc.vitalographwebportaluser:')) {
            return JSON.parse(window.sessionStorage.getItem(window.sessionStorage.key(i)));
        }
    }
}

export const useWebPortalLegacyClient = (): WebPortalLegacyClientInternal => {
    const auth = useAuth();

    const legacyClientFactory = (user) => {
        if (!user)
            return null;

        return new WebPortalLegacyClientInternal(rootUrl, {
            fetch: (url, init) => {

                const authInit = user?.access_token
                    ? {
                        ...init, credentials: 'same-origin',
                        headers: { ...init?.headers, 'Authorization': `Bearer ${GetTokenManually()?.access_token ?? user?.access_token}` }
                    } as RequestInit
                    : init;

                return fetch(url, authInit);
            }
        });
    }

    const [client, setClient] = React.useState<WebPortalLegacyClientInternal>(legacyClientFactory(auth.user));

    React.useEffect(() => {
        setClient(legacyClientFactory(auth.user));
    }, [auth.user?.access_token]);

    return client;
}

export interface WebPortalApiClient {
    client: WebPortalClient;
    abortController: AbortController;
}

export const useWebPortalApiClient = (): WebPortalApiClient => {
    const auth = useAuth();
    const abortController = new AbortController();

    const clientFactory = (user) => {
        return new WebPortalClient(undefined, {
            fetch: (url, init) => {



                const authInit = user?.access_token
                    ? {
                        ...init,
                        credentials: 'same-origin',
                        headers: { ...init?.headers, 'Authorization': `Bearer ${GetTokenManually()?.access_token ?? user?.access_token}` },
                        signal: abortController?.signal,
                    } as RequestInit
                    : init;

                return fetch(url, authInit);
            }
        });
    }

    const [client, setClient] = React.useState<WebPortalClient>(clientFactory(auth.user));

    React.useEffect(() => {
        setClient(clientFactory(auth.user));
    }, [auth.user.access_token]);

    return { client, abortController };
}
